import { c as _c } from "react/compiler-runtime";
import { useGetFaqCategoriesQuery, useGetFaqCategoryQuery, useGetFaqNextCategoriesQuery } from '@/api/sanity/generated';
import { ContentContainer, Layout } from '@/components/layout';
import { LINKS } from '@/constants/links';
import HeroSimple from '@/sections/HeroSimple';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import { isNil } from '@/utils/types';
import type { GetStaticProps } from 'next';
import { DetailQuestion } from '@/sections/faq/DetailQuestion';
import { usePageView } from '@/analytics/pageTypes';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { type Category } from '@/sections/faq/NextCategory';
import { NextCategories } from '@/sections/faq/NextCategories';
import { SeoHeader } from '@/components/SeoHeader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { sanityLanguage } from '@/constants/language';
interface PageProps extends IntlProps {
  category: Category;
  nextCategories: Category[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(34);
  const {
    category,
    nextCategories,
    menuPromoBanners
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["faq"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const t1 = category.name;
  let t2;
  if ($[1] !== category.slug.current) {
    t2 = LINKS().faqCategory(category.slug.current || "");
    $[1] = category.slug.current;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== category.name || $[4] !== t2) {
    t3 = {
      title: t1,
      url: t2
    };
    $[3] = category.name;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  usePageView("faq-category", t3);
  let t4;
  if ($[6] !== category.name || $[7] !== t) {
    t4 = t("faq:seo.categoryTitle", {
      category: category.name
    });
    $[6] = category.name;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("faq:seo.description");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <SeoHeader title={t4} description={t5} />;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      analyticsParam: "faqCategoryBack",
      testId: "faqCategoryBack",
      to: LINKS().faq
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== category.name) {
    t8 = <HeroSimple titleClassName="text-xl lg:text-5xl" circleClassName="left-auto right-0 lg:-translate-y-1/3 lg:translate-x-1/3 lg:h-[35rem] lg:w-[35rem]" title={category.name} withoutCharacters={true} backLink={t7} className="overflow-hidden bg-blue-50" />;
    $[15] = category.name;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== category.questions || $[18] !== category.slug.current) {
    t9 = category.questions?.map((questionItem, index) => {
      if (!questionItem?.name) {
        return null;
      }
      return <DetailQuestion variant="closed" key={index} {...questionItem} slug={{
        current: LINKS().faqQuestion(category.slug.current || "", questionItem.slug?.current || "")
      }} />;
    });
    $[17] = category.questions;
    $[18] = category.slug.current;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <ContentContainer className="relative z-10 flex flex-col gap-2 px-4 md:max-w-192 md:gap-4">{t9}</ContentContainer>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="bgCircle absolute bottom-0 left-0 hidden -translate-x-1/4 rotate-90 lg:block" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10) {
    t12 = <div className="relative bg-blue-50 pb-20 md:pb-40">{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== nextCategories) {
    t14 = <NextCategories nextCategories={nextCategories} />;
    $[26] = nextCategories;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== menuPromoBanners || $[29] !== t12 || $[30] !== t14 || $[31] !== t6 || $[32] !== t8) {
    t15 = <Layout menuPromoBanners={menuPromoBanners}>{t6}{t8}{t12}{t13}{t14}</Layout>;
    $[28] = menuPromoBanners;
    $[29] = t12;
    $[30] = t14;
    $[31] = t6;
    $[32] = t8;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}
export const getStaticPaths = async () => {
  const fetchAllCategories = useGetFaqCategoriesQuery.fetcher({
    i18nLang: sanityLanguage
  });
  const categories = await fetchAllCategories();
  const paths = categories.allFaqCategory.filter(item => item && item.name).map(category => ({
    params: {
      category: category?.slug?.current?.trim()
    }
  }));
  return {
    paths: paths?.filter(Boolean) || [],
    fallback: false
  };
};
const nextCategoriesIds = () => {
  if (process.env.NEXT_PUBLIC_LANGUAGE === 'hu') {
    return ['d97454e4-3ad8-449f-a190-5f2d7b592a5b', '2dd27096-53ce-4787-8e05-b4f818d9032a', 'd3b1f108-a23b-4a72-96bf-beac131a8b2a', '3a390f22-231f-48dd-a880-4c7adbae869a'];
  }
  if (process.env.NEXT_PUBLIC_LANGUAGE === 'sk') {
    return ['6BfMs6m5qGEYn3qsOWCBbG', '6Iyu1doyxlpMLwhkPsujmK', '2DwUXOK4Tws9NuPXiKBTbl', '12vL2KlrTy7tqbc1qWLwI4'];
  }
  if (process.env.NEXT_PUBLIC_TARGET_ENV === 'accept' || process.env.NEXT_PUBLIC_TARGET_ENV === 'nightly') {
    return ['6nV2Tpb7PwTJiS4RSLte7Z', '1KoTGhGVcmlZQQIr6IZbwo', '2tVFp1Ng5ctS7b0HvvN1uY'];
  }
  return ['7iCsxLitHoUqqRyE9ULjwV', '3mOvmE8yTjrrDsZJJyX4tJ', '4nQV5zahsGsZ7TiGXSSWbZ', '6nV2Tpb7PwTJiS4RSLte7Z'];
};
export const getStaticProps = (async ctx => {
  const categorySlug = ctx.params?.category as string;
  const fetchCategory = useGetFaqCategoryQuery.fetcher({
    slug: categorySlug,
    i18nLang: sanityLanguage
  });
  const fetchNextCategories = useGetFaqNextCategoriesQuery.fetcher({
    ids: nextCategoriesIds(),
    i18nLang: sanityLanguage
  });
  const categoryData = await fetchCategory();
  const nextCategoriesData = await fetchNextCategories();
  const category = categoryData.allFaqCategory[0] as Category | undefined;
  if (isNil(category)) {
    return {
      notFound: true
    };
  }
  const nextCategories = nextCategoriesData.allFaqCategory.filter(category => category && category.name && category.slug?.current !== categorySlug).slice(0, 3) as Category[];
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      category,
      nextCategories,
      ...(await getIntlProps(ctx, ['faq']))
    }
  };
}) satisfies GetStaticProps<PageProps>;